import React, { useState } from "react";
import WebAuthService from "../services/webAuthService";
import "./LoginPage.css";

interface LoginPageProps {
  onAuthenticated: () => void;
}

const LoginPage: React.FC<LoginPageProps> = ({ onAuthenticated }) => {
  const [mode, setMode] = useState<"login" | "register">("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [verificationSent, setVerificationSent] = useState(false);
  const [verificationCode, setVerificationCode] = useState("");

  const marketingUrl = window.location.hostname.includes("dev")
    ? "https://dev.ganttprojectplanner.com"
    : "https://ganttprojectplanner.com";

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (mode === "register" && password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const auth = WebAuthService.getInstance();
      if (mode === "register") {
        if (!verificationSent) {
          // Send verification email first
          const res = await fetch(`${process.env.REACT_APP_FUNC_ENDPOINT || ""}/api/auth/send-verification`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ email }),
          });
          const data = await res.json();
          if (!res.ok) throw new Error(data.error || "Failed to send verification email");
          setVerificationSent(true);
          setLoading(false);
          return;
        }
        // Verify code and register
        await auth.register(email, password, displayName, verificationCode);
      } else {
        await auth.login(email, password);
      }
      onAuthenticated();
    } catch (err: any) {
      setError(err.message || "Something went wrong");
    }
    setLoading(false);
  }

  async function handleMicrosoftLogin() {
    setLoading(true);
    setError(null);
    try {
      await WebAuthService.getInstance().startMicrosoftLogin();
    } catch (err: any) {
      setError(err.message || "Login failed");
      setLoading(false);
    }
  }

  return (
    <div className="login-page">
      <div className="login-card">
        <div className="login-header">
          <h1>Gantt Project Planner</h1>
          <p>{mode === "register" ? "Create your account" : "Sign in to your account"}</p>
        </div>

        <button
          className="login-microsoft-btn"
          onClick={handleMicrosoftLogin}
          disabled={loading}
        >
          <svg width="20" height="20" viewBox="0 0 21 21" xmlns="http://www.w3.org/2000/svg">
            <rect x="1" y="1" width="9" height="9" fill="#f25022"/>
            <rect x="11" y="1" width="9" height="9" fill="#7fba00"/>
            <rect x="1" y="11" width="9" height="9" fill="#00a4ef"/>
            <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
          </svg>
          Sign in with Microsoft
        </button>

        <div className="login-divider">
          <span>or</span>
        </div>

        <form onSubmit={handleSubmit} className="login-form">
          {mode === "register" && !verificationSent && (
            <>
              <input
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="Display name"
                required
              />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email"
                required
              />
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Password (8+ characters)"
                required
                minLength={8}
              />
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm password"
                required
                minLength={8}
              />
              <button type="submit" className="login-submit-btn" disabled={loading || !email || !password || !displayName || password !== confirmPassword}>
                {loading ? "Sending verification..." : "Continue"}
              </button>
              {password && confirmPassword && password !== confirmPassword && (
                <p className="login-error" style={{ marginTop: 0 }}>Passwords do not match</p>
              )}
            </>
          )}
          {mode === "register" && verificationSent && (
            <>
              <p className="login-verification-msg">We sent a verification code to <strong>{email}</strong>. Enter it below to complete registration.</p>
              <input
                type="text"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
                placeholder="Verification code"
                required
                autoFocus
              />
              <button type="submit" className="login-submit-btn" disabled={loading || !verificationCode}>
                {loading ? "Creating account..." : "Create Account"}
              </button>
            </>
          )}
          {mode === "login" && (
            <>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email"
                required
              />
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Password"
                required
              />
              <button type="submit" className="login-submit-btn" disabled={loading}>
                {loading ? "Please wait..." : "Sign In"}
              </button>
            </>
          )}
        </form>

        {error && <p className="login-error">{error}</p>}

        <p className="login-toggle">
          {mode === "login" ? (
            <>Don't have an account? <button onClick={() => { setMode("register"); setError(null); }}>Sign up</button></>
          ) : (
            <>Already have an account? <button onClick={() => { setMode("login"); setError(null); }}>Sign in</button></>
          )}
        </p>

        <p className="login-footer">
          <a href={marketingUrl}>Back to home</a>
        </p>
      </div>
    </div>
  );
};

export default LoginPage;
